import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { storesAPI, mediaAPI, uploadToCloudinary } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Upload,
  Image,
  Video,
  Trash2,
  Phone,
  MapPin,
  FileText,
  Calendar,
  User,
  Building,
  X,
  Loader2,
  Play
} from 'lucide-react';

export const StoreDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [mediaToDelete, setMediaToDelete] = useState(null);
  const [previewMedia, setPreviewMedia] = useState(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [storeRes, mediaRes] = await Promise.all([
        storesAPI.get(id),
        mediaAPI.list(id)
      ]);
      setStore(storeRes.data);
      setMedia(mediaRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados da loja');
      navigate('/admin/lojas');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    const totalFiles = files.length;
    let completed = 0;

    for (const file of files) {
      try {
        const isVideo = file.type.startsWith('video/');
        const resourceType = isVideo ? 'video' : 'image';

        // Upload to Cloudinary
        const result = await uploadToCloudinary(file, resourceType, 'stores');

        if (result.secure_url) {
          // Save to database
          await mediaAPI.create({
            store_id: id,
            url: result.secure_url,
            public_id: result.public_id,
            resource_type: resourceType,
            filename: file.name
          });
        }

        completed++;
        setUploadProgress(Math.round((completed / totalFiles) * 100));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Erro ao enviar ${file.name}`);
      }
    }

    setUploading(false);
    setUploadProgress(0);
    loadData();
    toast.success('Upload concluído!');
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files);
    handleFileSelect(files);
  }, [id]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
  }, []);

  const handleDeleteMedia = async () => {
    try {
      await mediaAPI.delete(mediaToDelete.id);
      toast.success('Mídia excluída com sucesso!');
      setDeleteDialogOpen(false);
      setMediaToDelete(null);
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir mídia');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      ativo: { class: 'active', label: 'Ativo' },
      vencido: { class: 'expired', label: 'Vencido' },
      suspenso: { class: 'suspended', label: 'Suspenso' }
    };
    const s = statusMap[status] || statusMap.ativo;
    return <span className={`status-badge ${s.class}`}>{s.label}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!store) {
    return null;
  }

  return (
    <div className="space-y-6" data-testid="store-details-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/lojas')} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">{store.name}</h1>
          <p className="text-slate-500">CNPJ: {store.cnpj}</p>
        </div>
        {getStatusBadge(store.contract_status)}
      </div>

      {/* Store Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
              <User className="w-4 h-4" />
              Responsável
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold text-slate-900">{store.responsible_name}</p>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Endereço
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-900">{store.address}</p>
            <p className="text-slate-600">{store.city}/{store.state} - CEP: {store.cep}</p>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
              <Phone className="w-4 h-4" />
              Contato
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-900">Tel: {store.phone}</p>
            <p className="text-slate-600">WhatsApp: {store.whatsapp}</p>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Contrato
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold text-slate-900">#{store.contract_number}</p>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Vigência
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-900">
              {new Date(store.contract_start).toLocaleDateString('pt-BR')} até {new Date(store.contract_end).toLocaleDateString('pt-BR')}
            </p>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
              <Building className="w-4 h-4" />
              Estatísticas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-900">Pedidos: <strong>{store.total_orders || 0}</strong></p>
            <p className="text-green-600">Receita: <strong>
              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(store.total_revenue || 0)}
            </strong></p>
          </CardContent>
        </Card>
      </div>

      {/* Media Gallery */}
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl font-semibold text-slate-900">
              Galeria de Mídia
            </CardTitle>
            <label>
              <input
                type="file"
                multiple
                accept="image/*,video/*"
                className="hidden"
                onChange={(e) => handleFileSelect(Array.from(e.target.files))}
                disabled={uploading}
                data-testid="file-input"
              />
              <Button
                as="span"
                className="bg-blue-600 hover:bg-blue-700 cursor-pointer"
                disabled={uploading}
                data-testid="upload-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? `Enviando... ${uploadProgress}%` : 'Adicionar Mídia'}
              </Button>
            </label>
          </div>
        </CardHeader>
        <CardContent>
          {/* Upload Zone */}
          <div
            className={`upload-zone mb-6 ${dragging ? 'dragging' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            data-testid="upload-zone"
          >
            <Upload className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            <p className="text-slate-600 font-medium">Arraste fotos e vídeos aqui</p>
            <p className="text-slate-400 text-sm mt-1">ou clique no botão acima para selecionar</p>
          </div>

          {/* Media Grid */}
          {media.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Image className="w-16 h-16 mx-auto mb-4" />
              <p className="font-medium">Nenhuma mídia cadastrada</p>
              <p className="text-sm">Adicione fotos e vídeos da loja</p>
            </div>
          ) : (
            <div className="media-grid">
              {media.map((item) => (
                <div
                  key={item.id}
                  className="media-item group"
                  onClick={() => setPreviewMedia(item)}
                  data-testid={`media-item-${item.id}`}
                >
                  {item.resource_type === 'video' ? (
                    <>
                      <video src={item.url} />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-12 h-12 bg-black/50 rounded-full flex items-center justify-center">
                          <Play className="w-6 h-6 text-white ml-1" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <img src={item.url} alt={item.filename} />
                  )}
                  <div className="media-item-overlay">
                    <div className="flex items-center justify-between w-full">
                      <span className="text-white text-sm truncate flex-1 mr-2">
                        {item.filename}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setMediaToDelete(item);
                          setDeleteDialogOpen(true);
                        }}
                        className="p-2 bg-red-600 rounded-full hover:bg-red-700 transition-colors"
                        data-testid={`delete-media-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={!!previewMedia} onOpenChange={() => setPreviewMedia(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          <button
            className="absolute top-4 right-4 z-10 p-2 bg-black/50 rounded-full hover:bg-black/70 transition-colors"
            onClick={() => setPreviewMedia(null)}
          >
            <X className="w-5 h-5 text-white" />
          </button>
          {previewMedia?.resource_type === 'video' ? (
            <video
              src={previewMedia?.url}
              controls
              autoPlay
              className="w-full max-h-[80vh]"
            />
          ) : (
            <img
              src={previewMedia?.url}
              alt={previewMedia?.filename}
              className="w-full max-h-[80vh] object-contain"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Tem certeza que deseja excluir esta mídia?
            Esta ação não pode ser desfeita.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDeleteMedia} data-testid="confirm-delete-media-btn">
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
